import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { useNavigate, useSearchParams } from "react-router-dom";

export default function AdminContactList() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const passcode = params.get("passcode") || "";
  const [items, setItems] = React.useState([]);
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [pagination, setPagination] = React.useState({ page: 1, pageSize: 20, total: 0, pages: 1 });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const newCount = React.useMemo(() => items.filter((it) => Boolean(it?.hasNewForAdmin)).length, [items]);

  const fetchThreads = React.useCallback(async ({ silent = false } = {}) => {
    if (!passcode) return;
    if (!silent) setLoading(true);
    try {
      const { getAdminContactConversations } = await import("../../lib/api");
      const data = await getAdminContactConversations({ passcode, search, page, pageSize: 20 });
      const nextItems = Array.isArray(data?.items) ? data.items : [];
      setItems(nextItems);
      setPagination(data?.pagination || { page: 1, pageSize: 20, total: 0, pages: 1 });
      setError("");
    } catch (_) {
      setError("Failed to fetch contact conversations. Check passcode.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [passcode, search, page]);

  React.useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  React.useEffect(() => {
    if (!passcode) return undefined;
    const timer = window.setInterval(() => {
      fetchThreads({ silent: true });
    }, 9000);
    return () => window.clearInterval(timer);
  }, [passcode, fetchThreads]);

  React.useEffect(() => {
    const base = "Admin • Contact Conversations";
    document.title = newCount > 0 ? `(${newCount}) ${base}` : base;
    return () => {
      document.title = "Somemas Recovery";
    };
  }, [newCount]);

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:py-10 space-y-6" data-testid="admin-contact-page">
      <Card className="border border-emerald-300/70 dark:border-emerald-700/50 bg-white dark:bg-slate-900 shadow-[0_10px_28px_rgba(16,185,129,0.10)]">
        <CardHeader>
          <CardTitle className="text-slate-900 dark:text-slate-100" data-testid="admin-contact-title">Contact Conversations</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!passcode && <div className="text-sm text-slate-500">Missing passcode. Go to /admin to generate links.</div>}

          {newCount > 0 && (
            <div className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="admin-contact-new-message-notification">
              New messages waiting: <span className="font-semibold">{newCount}</span>
            </div>
          )}

          <div className="flex items-center gap-2" data-testid="admin-contact-search-row">
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search by name or latest message"
              className="w-full max-w-sm rounded-md border border-emerald-300 dark:border-emerald-700 bg-emerald-50/60 dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400"
              data-testid="admin-contact-search-input"
            />
          </div>

          {loading && <div className="text-sm" data-testid="admin-contact-loading">Loading…</div>}
          {error && <div className="text-sm text-red-600" data-testid="admin-contact-error">{error}</div>}

          {!loading && !error && (
            <div className="border border-emerald-300/70 dark:border-emerald-700/50 rounded-xl p-2 md:p-3 bg-emerald-50/50 dark:bg-slate-950/40" data-testid="admin-contact-thread-list">
              {items.length === 0 && <div className="text-sm text-slate-500 p-3">No conversations yet.</div>}
              <div className="space-y-2">
                {items.map((thread) => (
                  <button
                    key={thread.id}
                    className="w-full text-left p-2.5 rounded-lg border border-emerald-300/70 dark:border-emerald-700/40 bg-emerald-100/70 dark:bg-emerald-900/20 hover:bg-emerald-200/60 dark:hover:bg-emerald-900/30 transition"
                    onClick={() => nav(`/admin/contact/thread/${encodeURIComponent(thread.id)}?passcode=${encodeURIComponent(passcode)}`)}
                    data-testid={`admin-contact-thread-pill-${thread.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold text-sm truncate text-slate-900 dark:text-slate-100">{thread.name}</div>
                      <div className="text-[11px] text-slate-600 dark:text-slate-300">{new Date(thread.updatedAt).toLocaleDateString()}</div>
                    </div>
                    <div className="mt-1 text-xs text-slate-700 dark:text-slate-200 line-clamp-1" data-testid={`admin-contact-thread-preview-${thread.id}`}>{thread.latestMessage}</div>
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <div className="text-[11px] text-slate-600 dark:text-slate-300">{thread.messageCount} messages</div>
                      {thread.hasNewForAdmin && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-600 text-white" data-testid={`admin-contact-thread-new-badge-${thread.id}`}>New</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between" data-testid="admin-contact-pagination-row">
            <div className="text-xs text-slate-500">Page {pagination.page} of {pagination.pages}</div>
            <div className="flex items-center gap-2">
              <button className="px-2 py-1 text-xs rounded border" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={pagination.page <= 1} data-testid="admin-contact-prev-page">Prev</button>
              <button className="px-2 py-1 text-xs rounded border" onClick={() => setPage((p) => Math.min(pagination.pages || 1, p + 1))} disabled={pagination.page >= (pagination.pages || 1)} data-testid="admin-contact-next-page">Next</button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}